import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import {
  COMMUNITY_CATEGORIES,
  createCommunityPost,
  addEncouragement,
  deleteCommunityPost,
  getCategoryMeta,
  isCommunityApiConfigured,
  loadCommunityFeed,
  loadCommunityProfile,
  reportCommunityPost,
  saveCommunityProfile,
  validateCommunityUserId,
} from "../storage/communityStorage.js";
import { useLanguage } from "../i18n/LanguageContext.js";
import { touchUI } from "../utils/touchUI.js";

function formatWhen(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

/**
 * @param {{ onBack?: () => void, embedded?: boolean }} props
 */
export function CommunityScreen({ onBack, embedded = false }) {
  const liveCommunity = isCommunityApiConfigured();
  const { t } = useLanguage();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [feedError, setFeedError] = useState("");
  const [userIdDraft, setUserIdDraft] = useState("");
  const [feed, setFeed] = useState([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeCategory, setComposeCategory] = useState("experience");
  const [composeBody, setComposeBody] = useState("");
  const [encouragePostId, setEncouragePostId] = useState(null);
  const [encourageText, setEncourageText] = useState("");

  const refresh = useCallback(async () => {
    try {
      const posts = await loadCommunityFeed();
      setFeed(posts);
      setFeedError("");
    } catch (e) {
      setFeedError(e?.message || "Could not load community feed.");
    }
  }, []);

  useEffect(() => {
    (async () => {
      const p = await loadCommunityProfile();
      // Live mode requires a session token — clear stale nickname-only profiles.
      if (liveCommunity && p?.userId && !p.token) {
        setProfile(null);
      } else {
        setProfile(p);
      }
      await refresh();
      setLoading(false);
    })();
  }, [refresh, liveCommunity]);

  const joinCommunity = async () => {
    const check = validateCommunityUserId(userIdDraft);
    if (!check.ok) {
      Alert.alert(t("community_join_title"), check.error || "Invalid user ID");
      return;
    }
    setJoining(true);
    try {
      const p = await saveCommunityProfile(userIdDraft);
      setProfile(p);
      await refresh();
      Alert.alert(
        t("community_welcome"),
        t("community_welcome_body").replace("@{id}", `@${p.userId}`),
      );
    } catch (e) {
      const msg = e?.message || String(e);
      const status = e?.status;
      if (status === 409 || msg.toLowerCase().includes("taken")) {
        Alert.alert(
          t("community_nickname_taken_title"),
          t("community_nickname_taken_body"),
        );
      } else if (msg.toLowerCase().includes("network") || msg.toLowerCase().includes("fetch") || msg.toLowerCase().includes("timeout")) {
        Alert.alert(
          t("community_waking_title"),
          t("community_waking_body"),
        );
      } else {
        Alert.alert(t("community_join_error_title"), msg);
      }
    } finally {
      setJoining(false);
    }
  };

  const onDeletePost = (post) => {
    Alert.alert(t("community_delete_title"), t("community_delete_body"), [
      { text: t("generic_cancel"), style: "cancel" },
      {
        text: "Delete",
        style: "destructive",
        onPress: () => {
          void (async () => {
            try {
              await deleteCommunityPost(post.id, profile.userId);
              await refresh();
            } catch (e) {
              Alert.alert(t("community_could_not_delete"), e?.message || String(e));
            }
          })();
        },
      },
    ]);
  };

  const onReportPost = (post) => {
    Alert.alert(t("community_report_title"), t("community_report_body"), [
      { text: t("generic_cancel"), style: "cancel" },
      {
        text: "Report",
        style: "destructive",
        onPress: () => {
          void (async () => {
            try {
              await reportCommunityPost(post.id, "user_report");
              Alert.alert(t("community_report_thanks"), t("community_report_thanks_body"));
              await refresh();
            } catch (e) {
              Alert.alert(t("community_could_not_report"), e?.message || String(e));
            }
          })();
        },
      },
    ]);
  };

  const publishPost = async () => {
    if (!profile?.userId) return;
    try {
      await createCommunityPost(composeCategory, composeBody, profile.userId);
      setComposeBody("");
      setComposeOpen(false);
      await refresh();
      Alert.alert(t("community_posted"), t("community_posted_body"));
    } catch (e) {
      Alert.alert(t("community_could_not_post"), e?.message || String(e));
    }
  };

  const sendEncouragement = async () => {
    if (!profile?.userId || !encouragePostId) return;
    try {
      await addEncouragement(encouragePostId, profile.userId, encourageText);
      setEncourageText("");
      setEncouragePostId(null);
      await refresh();
    } catch (e) {
      Alert.alert(t("community_could_not_send"), e?.message || String(e));
    }
  };

  if (loading) {
    return (
      <View>
        <Text style={styles.title}>{t("community_title")}</Text>
        <Text style={styles.muted}>{t("community_loading")}</Text>
        {!embedded && onBack ? (
        <Pressable style={styles.backBtn} onPress={onBack}>
          <Text style={styles.backBtnText}>{t("community_back")}</Text>
        </Pressable>
        ) : null}
      </View>
    );
  }

  if (!profile?.userId) {
    return (
      <KeyboardAvoidingView behavior={Platform.OS === "ios" ? "padding" : undefined}>
        <Text style={styles.title}>{t("community_join_title")}</Text>
        <Text style={styles.intro}>{t("community_intro_join")}</Text>
        <Text style={styles.label}>{t("community_nickname")}</Text>
        <TextInput
          style={styles.input}
          value={userIdDraft}
          onChangeText={setUserIdDraft}
          placeholder={t("community_nickname_placeholder")}
          autoCapitalize="none"
          autoCorrect={false}
          maxLength={24}
        />
        <Text style={styles.hint}>{t("community_nickname_hint")}</Text>
        <Pressable
          style={[styles.primaryBtn, joining && styles.primaryBtnDisabled]}
          onPress={() => void joinCommunity()}
          disabled={joining}
        >
          <Text style={styles.primaryBtnText}>
            {joining ? t("community_joining") : t("community_join_btn")}
          </Text>
        </Pressable>
        {!embedded && onBack ? (
        <Pressable style={styles.backBtn} onPress={onBack}>
          <Text style={styles.backBtnText}>{t("community_back")}</Text>
        </Pressable>
        ) : null}
      </KeyboardAvoidingView>
    );
  }

  return (
    <>
      <View style={styles.headerRow}>
        <View style={{ flex: 1 }}>
          <Text style={styles.title}>{t("community_title")}</Text>
          <Text style={styles.signedIn}>
            {t("community_signed_in")} @{profile.userId}
          </Text>
        </View>
        <Pressable style={styles.composeBtn} onPress={() => setComposeOpen(true)}>
          <Text style={styles.composeBtnText}>{t("community_share")}</Text>
        </Pressable>
      </View>

      <Text style={styles.intro}>{t("community_intro_feed")}</Text>

      {feedError ? <Text style={styles.errorText}>{feedError}</Text> : null}

      <ScrollView
        style={styles.feed}
        contentContainerStyle={{ paddingBottom: 24 }}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={() => {
              setRefreshing(true);
              void refresh().finally(() => setRefreshing(false));
            }}
          />
        }
      >
        {feed.map((post) => {
          const meta = getCategoryMeta(post.category);
          const isMine = profile?.userId && post.userId === profile.userId;
          return (
            <View key={post.id} style={styles.card}>
              <View style={styles.cardHeader}>
                <Text style={styles.cardCategory}>
                  {meta.emoji} {meta.label}
                </Text>
                <Text style={styles.cardMeta}>
                  @{post.userId} · {formatWhen(post.createdAt)}
                </Text>
              </View>
              <Text style={styles.cardBody}>{post.body}</Text>
              {(post.encouragements || []).length > 0 ? (
                <View style={styles.encourageList}>
                  {(post.encouragements || []).map((enc) => (
                    <View key={enc.id} style={styles.encourageItem}>
                      <Text style={styles.encourageUser}>@{enc.userId}</Text>
                      <Text style={styles.encourageText}>{enc.text}</Text>
                    </View>
                  ))}
                </View>
              ) : null}
              <View style={styles.cardActions}>
                <Pressable style={styles.encourageBtn} onPress={() => setEncouragePostId(post.id)}>
                  <Text style={styles.encourageBtnText}>💬 {t("community_encourage")}</Text>
                </Pressable>
                {isMine ? (
                  <Pressable style={styles.encourageBtn} onPress={() => onDeletePost(post)}>
                    <Text style={styles.dangerActionText}>{t("community_delete")}</Text>
                  </Pressable>
                ) : liveCommunity ? (
                  <Pressable style={styles.encourageBtn} onPress={() => onReportPost(post)}>
                    <Text style={styles.reportActionText}>{t("community_report")}</Text>
                  </Pressable>
                ) : null}
              </View>
            </View>
          );
        })}
        {feed.length === 0 && !feedError ? (
          <Text style={styles.emptyFeed}>{t("community_empty")}</Text>
        ) : null}
      </ScrollView>

      {!embedded && onBack ? (
      <Pressable style={styles.backBtn} onPress={onBack}>
        <Text style={styles.backBtnText}>{t("community_back")}</Text>
      </Pressable>
      ) : null}

      <Modal visible={composeOpen} animationType="slide" onRequestClose={() => setComposeOpen(false)}>
        <KeyboardAvoidingView style={styles.modalFlex} behavior={Platform.OS === "ios" ? "padding" : undefined}>
          <ScrollView contentContainerStyle={styles.modalScroll}>
            <Text style={styles.modalTitle}>{t("community_share_modal")}</Text>
            <Text style={styles.label}>{t("community_topic")}</Text>
            <View style={styles.chipWrap}>
              {COMMUNITY_CATEGORIES.map((c) => {
                const on = composeCategory === c.id;
                return (
                  <Pressable
                    key={c.id}
                    style={[styles.chip, on && styles.chipOn]}
                    onPress={() => setComposeCategory(c.id)}
                  >
                    <Text style={[styles.chipText, on && styles.chipTextOn]}>
                      {c.emoji} {c.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
            <Text style={styles.label}>{t("community_message")}</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={composeBody}
              onChangeText={setComposeBody}
              placeholder={t("community_post_placeholder")}
              multiline
              maxLength={2000}
              textAlignVertical="top"
            />
            <Pressable style={styles.primaryBtn} onPress={() => void publishPost()}>
              <Text style={styles.primaryBtnText}>{t("community_post_btn")}</Text>
            </Pressable>
            <Pressable style={styles.backBtn} onPress={() => setComposeOpen(false)}>
              <Text style={styles.backBtnText}>{t("generic_cancel")}</Text>
            </Pressable>
          </ScrollView>
        </KeyboardAvoidingView>
      </Modal>

      <Modal visible={!!encouragePostId} animationType="fade" transparent onRequestClose={() => setEncouragePostId(null)}>
        <View style={styles.encourageBackdrop}>
          <View style={styles.encourageCard}>
            <Text style={styles.modalTitle}>{t("community_encourage_title")}</Text>
            <TextInput
              style={[styles.input, styles.textAreaSmall]}
              value={encourageText}
              onChangeText={setEncourageText}
              placeholder={t("community_encourage_placeholder")}
              multiline
              maxLength={280}
            />
            <Pressable style={styles.primaryBtn} onPress={() => void sendEncouragement()}>
              <Text style={styles.primaryBtnText}>{t("community_send")}</Text>
            </Pressable>
            <Pressable style={styles.backBtn} onPress={() => setEncouragePostId(null)}>
              <Text style={styles.backBtnText}>{t("generic_cancel")}</Text>
            </Pressable>
          </View>
        </View>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  title: { fontSize: 24, fontWeight: "800", color: "#111", marginBottom: 4 },
  signedIn: { fontSize: 13, color: "#7C3AED", fontWeight: "700" },
  scopeHint: { fontSize: 11, color: "#64748B", marginTop: 4, fontWeight: "600" },
  scopeBadge: {
    alignSelf: "flex-start",
    backgroundColor: "#ECFDF5",
    borderWidth: 1,
    borderColor: "#A7F3D0",
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 4,
    marginBottom: 10,
  },
  scopeBadgeText: { fontSize: 11, fontWeight: "800", color: "#047857" },
  scopeBadgeLive: { backgroundColor: "#EDE9FE", borderColor: "#C4B5FD" },
  scopeBadgeTextLive: { color: "#5B21B6" },
  errorText: { fontSize: 12, color: "#B91C1C", marginBottom: 8, fontWeight: "600" },
  intro: { fontSize: 13, color: "#6B7280", lineHeight: 19, marginBottom: 12 },
  muted: { color: "#9CA3AF", marginBottom: 12 },
  headerRow: { flexDirection: "row", alignItems: "flex-start", marginBottom: 4 },
  composeBtn: {
    backgroundColor: "#7C3AED",
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 10,
  },
  composeBtnText: { color: "#fff", fontWeight: "800" },
  feed: { flexGrow: 1, minHeight: 280 },
  emptyFeed: { fontSize: 13, color: "#6B7280", textAlign: "center", marginTop: 24, lineHeight: 20 },
  card: {
    backgroundColor: "#FAF5FF",
    borderWidth: 1,
    borderColor: "#E9D5FF",
    borderRadius: 12,
    padding: 14,
    marginBottom: 10,
  },
  cardHeader: { marginBottom: 8 },
  cardCategory: { fontSize: 12, fontWeight: "800", color: "#6D28D9" },
  cardMeta: { fontSize: 11, color: "#9CA3AF", marginTop: 2 },
  cardBody: { fontSize: 14, color: "#1F2937", lineHeight: 20 },
  encourageList: {
    marginTop: 10,
    paddingTop: 8,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: "#E9D5FF",
  },
  encourageItem: { marginBottom: 6 },
  encourageUser: { fontSize: 11, fontWeight: "700", color: "#7C3AED" },
  encourageText: { fontSize: 13, color: "#374151", lineHeight: 18 },
  cardActions: { flexDirection: "row", alignItems: "center", gap: 16, marginTop: 8 },
  encourageBtn: { alignSelf: "flex-start" },
  encourageBtnText: { fontSize: 13, fontWeight: "700", color: "#7C3AED" },
  dangerActionText: { fontSize: 13, fontWeight: "700", color: "#B91C1C" },
  reportActionText: { fontSize: 13, fontWeight: "700", color: "#9A3412" },
  label: { fontSize: 13, fontWeight: "700", color: "#374151", marginBottom: 6, marginTop: 8 },
  hint: { fontSize: 11, color: "#9CA3AF", marginBottom: 12 },
  input: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    backgroundColor: "#FAFAFA",
  },
  textArea: { minHeight: 120, marginBottom: 8 },
  textAreaSmall: { minHeight: 80, marginBottom: 8 },
  primaryBtn: {
    backgroundColor: "#7C3AED",
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: "center",
    marginTop: 8,
  },
  primaryBtnText: { color: "#fff", fontWeight: "800" },
  primaryBtnDisabled: { opacity: 0.55 },
  backBtn: {
    backgroundColor: "#111",
    minHeight: touchUI.minHit,
    paddingVertical: touchUI.primaryPadV,
    paddingHorizontal: 16,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 10,
  },
  backBtnText: { color: "#fff", fontWeight: "800", fontSize: touchUI.primaryFont },
  chipWrap: { flexDirection: "row", flexWrap: "wrap", gap: 8, marginBottom: 8 },
  chip: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: "#F3F4F6",
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  chipOn: { backgroundColor: "#EDE9FE", borderColor: "#7C3AED" },
  chipText: { fontSize: 12, color: "#4B5563", fontWeight: "600" },
  chipTextOn: { color: "#5B21B6" },
  modalFlex: { flex: 1, backgroundColor: "#fff" },
  modalScroll: { padding: 20, paddingTop: Platform.OS === "ios" ? 52 : 28 },
  modalTitle: { fontSize: 20, fontWeight: "800", color: "#111", marginBottom: 12 },
  encourageBackdrop: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.45)",
    justifyContent: "center",
    padding: 20,
  },
  encourageCard: {
    backgroundColor: "#fff",
    borderRadius: 14,
    padding: 16,
  },
});
